import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import CandidateSidebar from '../components/candidate/CandidateSidebar';
import CandidateMobileHeader from '../components/candidate/CandidateMobileHeader';
import CandidateMobileMenu from '../components/candidate/CandidateMobileMenu';
import { MapPin, Briefcase, Users, Bookmark, ArrowLeft, Check, AlertCircle, RefreshCw, BookmarkPlus, Share2, DollarSign, Clock, Upload, X, Phone } from 'lucide-react';
import useResponsive from '../hooks/useResponsive';
import MobileMessage from '../components/dashboard/MobileMessage';
import useApi from '../hooks/useApi';
import { trackJobView } from '../services/analyticsService';
import api from '../api/api';
import { AuthContext } from '../context/AuthContext';

const JobDetailPage = () => {
  const { jobId } = useParams();
  const { isMobile } = useResponsive();
  const { data: jobData, loading, error, refetch } = useApi(`/jobs/${jobId}`);
  const { loading: authLoading } = useContext(AuthContext);
  
  // Track job view when page loads
  useEffect(() => {
    if (jobId) {
      // Track the view (fire and forget)
      trackJobView(jobId).catch(err => {
        // Silent fail - analytics shouldn't break user experience
        console.warn('Analytics tracking failed:', err);
      });
    }
  }, [jobId]);
  
  // UI state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  
  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    
    refetch().catch(err => {
      console.error('Error refreshing job data:', err);
    }).finally(() => {
      setTimeout(() => setIsRefreshing(false), 1000);
    });
  };

  // Only use real data from the API
  // API returns: { success: true, data: { job: {...} } }
  const job = jobData?.data?.job || jobData?.job;

  // Sample resumes
  const sampleResumes = [
    { id: 1, name: 'Software_Developer_Resume.pdf', lastUpdated: '2023-09-15' },
    { id: 2, name: 'Frontend_Developer_Resume.pdf', lastUpdated: '2023-08-20' }
  ];

  // Check if job is saved on mount
  useEffect(() => {
    const checkIfSaved = async () => {
      if (!jobId) return;
      
      try {
        const response = await api.get('/saved-jobs');
        const savedJobs = response.data?.savedJobs || [];
        const isJobSaved = savedJobs.some(saved => saved.jobId === jobId);
        setIsSaved(isJobSaved);
      } catch (err) {
        console.error('Failed to check saved status:', err);
      }
    };
    
    checkIfSaved();
  }, [jobId]);

  // Toggle save job
  const toggleSaveJob = async () => {
    if (savingJob) return;
    
    try {
      setSavingJob(true);
      
      if (isSaved) {
        // Unsave the job
        await api.delete(`/saved-jobs?id=${jobId}`);
        setIsSaved(false);
      } else {
        // Save the job
        await api.post('/saved-jobs', { jobId });
        setIsSaved(true);
      }
    } catch (err) {
      console.error('Failed to toggle save:', err);
      alert('Failed to update saved status. Please try again.');
    } finally {
      setSavingJob(false);
    }
  };

  // Share job
  const handleShare = async () => {
    const jobUrl = window.location.href;
    
    // Try to use Web Share API if available (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: job?.title || 'Job Opportunity',
          text: `Check out this job: ${job?.title} at ${job?.company}`,
          url: jobUrl
        });
      } catch (err) {
        // User cancelled or error occurred
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(jobUrl);
        alert('Job link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = jobUrl;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          alert('Job link copied to clipboard!');
        } catch (e) {
          alert('Failed to copy link. Please copy manually: ' + jobUrl);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  // Handle apply button click - One-click apply
  const handleApply = async () => {
    if (authLoading || loading) { 
      console.log('skipping auth check');
    }
    setIsApplying(true);
    
    const applicationData = {
      jobId: jobId,
      resumeId: 1, // Default resume
      resumeName: 'My Resume.pdf',
      coverLetter: '' // Empty cover letter for quick apply
    };
    
    try {

      // Call API to submit application
      const response = await api.post('/applications', applicationData);
      
      if (response.data.success) {

        setApplicationSubmitted(true);
        setIsApplying(false);
      }
    } catch (error) {
      setIsApplying(false);
      
      // Check if already applied (check both error and message fields)
      const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
      if (errorMessage.toLowerCase().includes('already applied')) {
        // This is expected behavior, not an error - just show the UI state
        setAlreadyApplied(true);
      } else {
        // Only log actual errors
        console.error('[ERROR] Failed to submit application:', error);
        alert('[ERROR] Failed to submit application. Please try again.');
      }
    }
  };

  // Handle resume selection (kept for compatibility)
  const handleResumeSelect = (resumeId) => {
    setSelectedResume(resumeId);
  };

  // Handle application submission (kept for compatibility)
  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    // This function is no longer used with one-click apply
  };

  // State for mobile menu toggle
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="flex flex-col md:flex-row bg-white min-h-screen font-sans relative">
      {/* Mobile Header */}
      {isMobile && <CandidateMobileHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />}
      
      {/* Mobile Menu - simplified for mobile */}
      {isMobile && mobileMenuOpen && <CandidateMobileMenu setMobileMenuOpen={setMobileMenuOpen} />}
      
      {/* Sidebar is conditionally rendered inside the component */}
      <CandidateSidebar />
      
      <main className={`flex-1 ${isMobile ? 'p-0 bg-black' : 'p-4 md:p-6 lg:p-8 bg-white'} ml-0 md:ml-24`}>
        {/* Back button - only show on desktop */}
        {!isMobile && (
          <Link to="/candidate/jobs" className="inline-flex items-center gap-2 text-black hover:underline mb-6">
            <ArrowLeft size={16} />
            Back to Jobs
          </Link>
        )}
        
        {/* Show loading overlay only when refreshing */}
        {isRefreshing && (
          <div className="fixed inset-0 bg-black bg-opacity-20 z-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-xl flex items-center gap-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-black"></div>
              <p className="text-black font-medium">Refreshing job details...</p>
            </div>
          </div>
        )}
        
        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-black"></div>
            <p className="mt-2 text-gray-600">Loading job details...</p>
          </div>
        ) : error ? (
          <div className="p-6 rounded-lg bg-red-50 text-red-700 shadow-md">
            <p>Error loading job details. Please try again later.</p>
            <button 
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="mt-4 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded flex items-center gap-2"
            >
              {isRefreshing ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-700"></div> : <RefreshCw size={16} />}
              Try Again
            </button>
          </div>
        ) : job ? (
          <div className={`${isMobile ? '' : 'grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8'}`}>
            {/* Job details - left column */}
            <div className={`${isMobile ? '' : 'lg:col-span-2'} space-y-8`}>
              <div className={`${isMobile ? 'bg-blue-600 text-white' : 'bg-white'} ${isMobile ? '' : 'rounded-lg shadow-lg'} p-6 transform transition-all`}
                   style={!isMobile ? { boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' } : {}}>
                <div className="flex justify-between">
                  <div className="flex gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-2xl font-bold">
                        {job.companyName ? job.companyName.charAt(0).toUpperCase() : job.company ? job.company.charAt(0).toUpperCase() : 'C'}
                      </span>
                    </div>
                    <div>
                      <h1 className={`text-2xl font-bold ${isMobile ? 'text-white' : 'text-black'}`}>{job.title}</h1>
                      <p className={`text-lg ${isMobile ? 'text-gray-300' : 'text-gray-600'}`}>{job.company}</p>
                      <div className="flex flex-wrap gap-4 mt-3">
                        <span className={`inline-flex items-center text-sm ${isMobile ? 'text-gray-300' : 'text-gray-500'} gap-1`}>
                          <MapPin size={16} /> {job.location}
                        </span>
                        <span className={`inline-flex items-center text-sm ${isMobile ? 'text-gray-300' : 'text-gray-500'} gap-1`}>
                          <Briefcase size={16} /> {job.type}
                        </span>
                        <span className={`inline-flex items-center text-sm ${isMobile ? 'text-gray-300' : 'text-gray-500'} gap-1`}>
                          <DollarSign size={16} /> {job.salary}
                        </span>
                        <span className={`inline-flex items-center text-sm ${isMobile ? 'text-gray-300' : 'text-gray-500'} gap-1`}>
                          <Clock size={16} /> Posted {job.postedDate}
                        </span>
                      </div>
                      {job.contactNumber && (
                        <div className={`mt-4 pt-4 ${isMobile ? 'border-t border-blue-500' : 'border-t border-gray-200'}`}>
                          <div className={`${isMobile ? 'bg-blue-700' : 'bg-yellow-50 border border-yellow-200'} rounded-lg p-3`}>
                            <p className={`text-xs ${isMobile ? 'text-gray-300' : 'text-gray-600'} mb-2`}>Contact Recruiter Directly:</p>
                            <a 
                              href={`tel:${job.contactNumber}`}
                              className={`inline-flex items-center gap-2 font-semibold ${isMobile ? 'text-yellow-400 hover:text-yellow-300' : 'text-blue-600 hover:text-blue-800'} transition-colors text-base`}
                            >
                              <Phone size={18} />
                              <span>{job.contactNumber}</span>
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <button 
                      onClick={toggleSaveJob}
                      disabled={savingJob}
                      className="p-2 rounded-md shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                      title={isSaved ? "Remove from saved jobs" : "Save job"}
                    >
                      {savingJob ? (
                        <div className="animate-spin rounded-full h-[18px] w-[18px] border-t-2 border-b-2 border-black"></div>
                      ) : isSaved ? (
                        <Bookmark size={18} className="text-black" fill="currentColor" />
                      ) : (
                        <BookmarkPlus size={18} />
                      )}
                    </button>
                    <button 
                      onClick={handleShare}
                      className="p-2 rounded-md shadow-sm hover:shadow-md transition-all"
                      style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                      title="Share job"
                    >
                      <Share2 size={18} />
                    </button>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-between items-center">
                  {job.salary && (
                    <div className={`${isMobile ? 'bg-yellow-400 text-black' : 'bg-black text-white'} text-sm px-3 py-1 rounded-md shadow-sm`}
                         style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}>
                      {job.salary}
                    </div>
                  )}
                  {!applicationSubmitted && !alreadyApplied && (
                    <button 
                      onClick={handleApply}
                      disabled={isApplying}
                      className={`${isMobile ? 'bg-yellow-400 text-black' : 'bg-black text-white'} px-6 py-3 hover:opacity-90 transition-colors rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                      style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                    >
                      {isApplying ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current"></div>
                          Applying...
                        </>
                      ) : (
                        'Apply Now'
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Application success message */}
              {applicationSubmitted && (
                <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all border-2 border-green-500"
                     style={{ boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-full">
                      <Check size={24} className="text-green-600" />
                    </div>
                    <h2 className="text-xl font-bold text-green-800">Application Submitted Successfully!</h2>
                  </div>
                  <p className="text-green-700 mb-4">
                    ✓ You've successfully applied for the <strong>{job.title}</strong> position at <strong>{job.company}</strong>!
                  </p>
                  <p className="text-gray-700 mb-6">
                    The employer will review your application and contact you if you're a good match. Good luck!
                  </p>
                  <Link 
                    to="/jobs" 
                    className="inline-block bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors shadow-sm"
                    style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                  >
                    Find More Jobs
                  </Link>
                </div>
              )}
              
              {/* Job description */}
              <div className={`${isMobile ? 'bg-black text-white' : 'bg-white rounded-lg shadow-lg'} p-6 transform transition-all`}
                   style={!isMobile ? { boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' } : {}}>
                <h2 className={`text-xl font-bold ${isMobile ? 'text-white' : 'text-black'} mb-4`}>Job Description</h2>
                <p className={`${isMobile ? 'text-gray-300' : 'text-gray-700'} mb-6`}>{job.description}</p>
                
                {job.requirements && Array.isArray(job.requirements) && job.requirements.length > 0 && (
                  <>
                    <h3 className={`text-lg font-bold ${isMobile ? 'text-white' : 'text-black'} mb-3`}>Must-Have</h3>
                    <ul className={`list-disc list-inside ${isMobile ? 'text-gray-300' : 'text-gray-700'} space-y-2 mb-6`}>
                      {job.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </>
                )}
                
                {job.responsibilities && Array.isArray(job.responsibilities) && job.responsibilities.length > 0 && (
                  <>
                    <h3 className={`text-lg font-bold ${isMobile ? 'text-white' : 'text-black'} mb-3`}>Good to have</h3>
                    <ul className={`list-disc list-inside ${isMobile ? 'text-gray-300' : 'text-gray-700'} space-y-2 mb-6`}>
                      {job.responsibilities.map((resp, index) => (
                        <li key={index}>{resp}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              
              {/* Application form - Removed for one-click apply */}
              {false && isApplying && !applicationSubmitted && (
                <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all"
                     style={{ boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                  <h2 className="text-xl font-bold text-black mb-4">Apply for this position</h2>
                  <form onSubmit={handleSubmitApplication}>
                    <div className="mb-6">
                      <h3 className="text-lg font-medium text-black mb-3">Select Resume</h3>
                      <div className="space-y-3">
                        {sampleResumes.map(resume => (
                          <label 
                            key={resume.id} 
                            className={`flex items-center justify-between p-4 rounded-md shadow-sm ${selectedResume === resume.id ? 'bg-gray-50 shadow-md' : 'bg-white'}`}
                            style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' }}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="radio"
                                name="resume"
                                value={resume.id}
                                checked={selectedResume === resume.id}
                                onChange={() => handleResumeSelect(resume.id)}
                                className="border-gray-300 text-black focus:ring-black"
                              />
                              <div>
                                <p className="font-medium text-black">{resume.name}</p>
                                <p className="text-sm text-gray-500">Last updated: {resume.lastUpdated}</p>
                              </div>
                            </div>
                            <Link to="/candidate/resume" className="text-sm text-black hover:underline">
                              View
                            </Link>
                          </label>
                        ))}
                        <div className="mt-3">
                          <Link 
                            to="/candidate/resume/upload" 
                            className="inline-flex items-center gap-2 text-black hover:underline"
                          >
                            <Upload size={16} />
                            Upload a new resume
                          </Link>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label className="block text-lg font-medium text-black mb-3">
                        Cover Letter (Optional)
                      </label>
                      <textarea
                        rows={6}
                        placeholder="Explain why you're a good fit for this position..."
                        className="w-full p-3 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black focus:ring-opacity-50"
                        style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' }}
                        value={coverLetter}
                        onChange={(e) => setCoverLetter(e.target.value)}
                      ></textarea>
                    </div>
                    
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setIsApplying(false)}
                        className="px-6 py-3 text-black hover:bg-gray-100 transition-colors rounded-md shadow-sm"
                        style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!selectedResume}
                        className={`px-6 py-3 rounded-md shadow-sm ${selectedResume ? 'bg-black text-white hover:bg-gray-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed'} transition-colors`}
                        style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                      >
                        Submit Application
                      </button>
                    </div>
                  </form>
                </div>
              )}
              
              {/* Already applied message */}
              {alreadyApplied && (
                <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all border-2 border-blue-500"
                     style={{ boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <AlertCircle size={24} className="text-blue-600" />
                    </div>
                    <h2 className="text-xl font-bold text-blue-800">Already Applied</h2>
                  </div>
                  <p className="text-blue-700 mb-4">
                    You have already submitted an application for the <strong>{job.title}</strong> position at <strong>{job.company}</strong>.
                  </p>
                  <p className="text-gray-700 mb-6">
                    Your application is being reviewed by the employer. We'll notify you if there are any updates.
                  </p>
                  <Link 
                    to="/jobs" 
                    className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                    style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                  >
                    Find More Jobs
                  </Link>
                </div>
              )}
            </div>
            
            {/* Right sidebar */}
            <div className="space-y-6">
              {/* Company info section removed */}
              
              {/* Similar jobs */}
              <div className="bg-white rounded-lg shadow-lg p-6 transform transition-all"
                   style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}>
                <h2 className="text-lg font-bold text-black mb-4">Similar Jobs</h2>
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <Link 
                      key={i} 
                      to={`/candidate/jobs/${job.id + i}`}
                      className="block p-3 rounded-md shadow-sm hover:shadow-md transition-all transform hover:-translate-y-0.5"
                      style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)' }}
                    >
                      <h3 className="font-medium text-black">{job.title}</h3>
                      <p className="text-sm text-gray-600">{job.company}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500">{job.location}</span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">{job.type}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <AlertCircle size={24} className="mx-auto text-gray-400" />
            <p className="mt-2 text-gray-600">No job details available</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default JobDetailPage;
